import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const API_URL      = process.env.REACT_APP_API_URL      || 'http://localhost:5002/api/v1';
const PAYMENTS_URL = process.env.REACT_APP_PAYMENTS_URL || 'http://localhost:5003';
const MEDIA_URL    = process.env.REACT_APP_MEDIA_URL    || 'http://localhost:5001';

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

const STEPS = [
  { id:'review',  label:'Review',  icon:'◈' },
  { id:'info',    label:'Details', icon:'◉' },
  { id:'payment', label:'Payment', icon:'◐' },
  { id:'confirm', label:'Confirm', icon:'✓' },
];

const inp = (err) => ({
  width:'100%', background:'var(--bg-2)', border:`1px solid ${err?'rgba(240,96,96,0.5)':'var(--border)'}`,
  borderRadius:8, color:'var(--t-primary)', padding:'11px 14px', fontSize:'0.9rem',
  outline:'none', transition:'border-color 0.15s', boxSizing:'border-box', fontFamily:'inherit',
});
const lbl      = { fontSize:'0.72rem', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--t-secondary)', display:'block', marginBottom:6 };
const errStyle = { fontSize:'0.75rem', color:'#ef4444', marginTop:4, display:'flex', alignItems:'center', gap:4 };
const backBtn  = { padding:'13px 20px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--t-secondary)', cursor:'pointer', fontSize:'0.9rem', fontFamily:'inherit', transition:'all 0.15s' };

// ── Step 1 — Cart Review ──────────────────────────────────
function ReviewStep({ items, total, currency, onNext }) {
  const { removeItem, updateQty } = useCart();
  if (items.length === 0) return (
    <div style={{ textAlign:'center', padding:'60px 0' }}>
      <div style={{ fontSize:'3rem', opacity:0.15, marginBottom:16 }}>◈</div>
      <p style={{ color:'var(--t-muted)', marginBottom:20 }}>Your cart is empty.</p>
      <Link to="/marketplace" className="btn btn-primary" style={{ margin:'0 auto' }}>Browse Marketplace →</Link>
    </div>
  );
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {items.map(item => {
        const img = resolveMedia(item.image);
        const key = item.variant ? `${item.id}_${item.variant}` : item.id;
        return (
          <div key={key} style={{ display:'flex', gap:14, padding:'16px', background:'var(--bg-2)', borderRadius:12, border:'1px solid var(--border)' }}>
            <div style={{ width:60, height:60, borderRadius:8, overflow:'hidden', background:'var(--bg-3)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {img ? <img src={img} alt={item.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <div style={{ fontSize:'1.5rem', opacity:0.2 }}>◈</div>}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:600, fontSize:'0.92rem', marginBottom:4 }}>{item.name}</div>
              <div style={{ fontSize:'0.75rem', color:'var(--t-muted)', marginBottom:8, textTransform:'capitalize' }}>
                {item.type}{item.variant ? ` · ${item.variant}` : ''}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ fontWeight:700, color:'var(--gold)' }}>
                  {item.is_free ? 'Free' : `${item.currency} ${Number(item.price * item.qty).toLocaleString()}`}
                </div>
                {item.type === 'physical' ? (
                  <div style={{ display:'flex', alignItems:'center', gap:0, background:'var(--bg-3)', borderRadius:8, border:'1px solid var(--border)', overflow:'hidden' }}>
                    <button onClick={() => updateQty(item.id, item.variant, item.qty-1)} style={{ width:28, height:28, background:'none', border:'none', cursor:'pointer', color:'var(--t-muted)', fontSize:'1rem', fontFamily:'inherit' }}>−</button>
                    <span style={{ width:28, textAlign:'center', fontSize:'0.82rem', fontWeight:600 }}>{item.qty}</span>
                    <button onClick={() => updateQty(item.id, item.variant, item.qty+1)} style={{ width:28, height:28, background:'none', border:'none', cursor:'pointer', color:'var(--t-muted)', fontSize:'1rem', fontFamily:'inherit' }}>+</button>
                  </div>
                ) : <span style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>×1</span>}
              </div>
            </div>
            <button onClick={() => removeItem(item.id, item.variant)} style={{ color:'var(--t-muted)', background:'none', border:'none', cursor:'pointer', fontSize:'0.9rem', alignSelf:'flex-start', padding:4, transition:'color 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.color='#ef4444'} onMouseLeave={e=>e.currentTarget.style.color='var(--t-muted)'}>✕</button>
          </div>
        );
      })}
      <div style={{ padding:'16px', background:'var(--bg-2)', borderRadius:12, border:'1px solid var(--border)', marginTop:4 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.85rem', marginBottom:8 }}>
          <span style={{ color:'var(--t-muted)' }}>Subtotal</span><span>{currency} {total.toLocaleString()}</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:'1rem', paddingTop:12, borderTop:'1px solid var(--border)' }}>
          <span>Total</span><span style={{ color:'var(--gold)' }}>{currency} {total.toLocaleString()}</span>
        </div>
      </div>
      <button onClick={onNext} className="btn btn-primary" style={{ padding:'14px', fontSize:'0.95rem', fontWeight:700, justifyContent:'center', marginTop:4 }}>
        Continue to Details →
      </button>
    </div>
  );
}

// ── Step 2 — Customer Info ────────────────────────────────
function InfoStep({ form, setForm, errors, onNext, onBack, hasPhysical }) {
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <div style={{ gridColumn:'1/-1' }}>
          <label style={lbl}>Full Name *</label>
          <input value={form.full_name||''} onChange={e=>set('full_name',e.target.value)} placeholder="Jean Claude Habimana" style={inp(errors.full_name)}/>
          {errors.full_name && <div style={errStyle}>{errors.full_name}</div>}
        </div>
        <div>
          <label style={lbl}>Email Address *</label>
          <input type="email" value={form.email||''} onChange={e=>set('email',e.target.value)} placeholder="you@email.com" style={inp(errors.email)}/>
          {errors.email && <div style={errStyle}>{errors.email}</div>}
        </div>
        <div>
          <label style={lbl}>Phone Number *</label>
          <input value={form.phone||''} onChange={e=>set('phone',e.target.value)} placeholder="0781234567" style={inp(errors.phone)}/>
          {errors.phone && <div style={errStyle}>{errors.phone}</div>}
        </div>
      </div>
      {hasPhysical && (
        <div>
          <label style={lbl}>Delivery Address *</label>
          <textarea value={form.address||''} onChange={e=>set('address',e.target.value)}
            placeholder="Street, Sector, District, Province" rows={3}
            style={{ ...inp(errors.address), resize:'none', lineHeight:1.6 }}/>
          {errors.address && <div style={errStyle}>{errors.address}</div>}
        </div>
      )}
      <div>
        <label style={lbl}>Order Notes (optional)</label>
        <textarea value={form.notes||''} onChange={e=>set('notes',e.target.value)}
          placeholder="Any special instructions…" rows={2}
          style={{ ...inp(false), resize:'none', lineHeight:1.6 }}/>
      </div>
      <div style={{ display:'flex', gap:10, marginTop:4 }}>
        <button onClick={onBack} style={backBtn}>← Back</button>
        <button onClick={onNext} className="btn btn-primary" style={{ flex:1, padding:'14px', fontSize:'0.95rem', fontWeight:700, justifyContent:'center' }}>
          Continue to Payment →
        </button>
      </div>
    </div>
  );
}

// ── Step 3 — Payment ──────────────────────────────────────
function PaymentStep({ form, setForm, total, currency, errors, onNext, onBack, submitting }) {
  const set    = (k,v) => setForm(f=>({...f,[k]:v}));
  const method = form.payment_method;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div>
        <label style={lbl}>Select Payment Method *</label>
        <div style={{ display:'flex', flexDirection:'column', gap:10, marginTop:6 }}>

          {/* MTN MoMo */}
          <button onClick={() => set('payment_method','mtn_momo')}
            style={{ display:'flex', alignItems:'center', gap:14, padding:'16px', borderRadius:12, border:`2px solid ${method==='mtn_momo'?'#f9c80e':'var(--border)'}`, background:method==='mtn_momo'?'rgba(249,200,14,0.06)':'var(--bg-2)', cursor:'pointer', textAlign:'left', transition:'all 0.15s', fontFamily:'inherit' }}>
            <div style={{ width:40, height:40, borderRadius:10, background:'rgba(249,200,14,0.15)', border:'1px solid rgba(249,200,14,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem', flexShrink:0 }}>📱</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:'0.9rem', color:'var(--t-primary)', marginBottom:2 }}>MTN Mobile Money</div>
              <div style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>Pay via USSD push to your MoMo number — instant confirmation</div>
            </div>
            <div style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${method==='mtn_momo'?'#f9c80e':'var(--border)'}`, background:method==='mtn_momo'?'#f9c80e':'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {method==='mtn_momo' && <div style={{ width:8, height:8, borderRadius:'50%', background:'#080a0d' }}/>}
            </div>
          </button>

          {/* Bank Transfer */}
          <button onClick={() => set('payment_method','bank_transfer')}
            style={{ display:'flex', alignItems:'center', gap:14, padding:'16px', borderRadius:12, border:`2px solid ${method==='bank_transfer'?'#4e9cf5':'var(--border)'}`, background:method==='bank_transfer'?'rgba(78,156,245,0.06)':'var(--bg-2)', cursor:'pointer', textAlign:'left', transition:'all 0.15s', fontFamily:'inherit' }}>
            <div style={{ width:40, height:40, borderRadius:10, background:'rgba(78,156,245,0.15)', border:'1px solid rgba(78,156,245,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem', flexShrink:0 }}>🏦</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:'0.9rem', color:'var(--t-primary)', marginBottom:2 }}>Bank Transfer</div>
              <div style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>Transfer to MilleHills account — confirmed within 24 hours</div>
            </div>
            <div style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${method==='bank_transfer'?'#4e9cf5':'var(--border)'}`, background:method==='bank_transfer'?'#4e9cf5':'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {method==='bank_transfer' && <div style={{ width:8, height:8, borderRadius:'50%', background:'#fff' }}/>}
            </div>
          </button>

          {/* Card via Pesapal */}
          <button onClick={() => set('payment_method','pesapal')}
            style={{ display:'flex', alignItems:'center', gap:14, padding:'16px', borderRadius:12, border:`2px solid ${method==='pesapal'?'#3ecf8e':'var(--border)'}`, background:method==='pesapal'?'rgba(62,207,142,0.06)':'var(--bg-2)', cursor:'pointer', textAlign:'left', transition:'all 0.15s', fontFamily:'inherit' }}>
            <div style={{ width:40, height:40, borderRadius:10, background:'rgba(62,207,142,0.1)', border:'1px solid rgba(62,207,142,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem', flexShrink:0 }}>💳</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:'0.9rem', color:'var(--t-primary)', marginBottom:2 }}>Card Payment</div>
              <div style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>Visa, Mastercard — local & international cards</div>
            </div>
            <div style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${method==='pesapal'?'#3ecf8e':'var(--border)'}`, background:method==='pesapal'?'#3ecf8e':'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {method==='pesapal' && <div style={{ width:8, height:8, borderRadius:'50%', background:'#080a0d' }}/>}
            </div>
          </button>
        </div>
        {errors.payment_method && <div style={errStyle}>{errors.payment_method}</div>}
      </div>

      {/* MoMo phone */}
      {method === 'mtn_momo' && (
        <div style={{ padding:'16px', background:'rgba(249,200,14,0.06)', border:'1px solid rgba(249,200,14,0.2)', borderRadius:12 }}>
          <label style={lbl}>MoMo Phone Number *</label>
          <input value={form.momo_phone||form.phone||''} onChange={e=>set('momo_phone',e.target.value)}
            placeholder="0781234567" style={inp(errors.momo_phone)}/>
          {errors.momo_phone && <div style={errStyle}>{errors.momo_phone}</div>}
          <div style={{ fontSize:'0.75rem', color:'rgba(249,200,14,0.8)', marginTop:8, lineHeight:1.6 }}>
            📱 You'll receive a USSD prompt on this number to approve <strong>{currency} {total.toLocaleString()}</strong>.
          </div>
        </div>
      )}

      {/* Bank Transfer details */}
      {method === 'bank_transfer' && (
        <div style={{ padding:'16px', background:'rgba(78,156,245,0.06)', border:'1px solid rgba(78,156,245,0.2)', borderRadius:12 }}>
          <div style={{ fontSize:'0.75rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12, fontWeight:600 }}>Bank Account Details</div>
          {[
            ['Bank',           'BPR Bank (Banque Populaire du Rwanda)'],
            ['Account Name',   'MilleHills Ltd'],
            ['Account Number', '491403162210193'],
            ['Branch',         'Gisenyi Branch'],
            ['Reference',      `ORDER-${Date.now().toString(36).toUpperCase()}`],
          ].map(([k,v]) => (
            <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.85rem', padding:'6px 0', borderBottom:'1px solid rgba(78,156,245,0.1)' }}>
              <span style={{ color:'var(--t-muted)' }}>{k}</span>
              <span style={{ fontWeight:600, fontFamily:k==='Account Number'||k==='Reference'?'monospace':'inherit' }}>{v}</span>
            </div>
          ))}
          <div style={{ fontSize:'0.75rem', color:'#4e9cf5', marginTop:10, lineHeight:1.6 }}>
            🏦 Send <strong>{currency} {total.toLocaleString()}</strong> using the reference above. Confirmed within 24 hours.
          </div>
        </div>
      )}

      {/* Pesapal info */}
      {method === 'pesapal' && (
        <div style={{ padding:'16px', background:'rgba(62,207,142,0.06)', border:'1px solid rgba(62,207,142,0.2)', borderRadius:12 }}>
          <div style={{ fontSize:'0.75rem', color:'#3ecf8e', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10, fontWeight:600 }}>Secure Card Payment</div>
          <div style={{ fontSize:'0.85rem', color:'var(--t-secondary)', lineHeight:1.7 }}>
            You'll be redirected to a secure Pesapal page to complete your payment of <strong style={{ color:'var(--t-primary)' }}>{currency} {total.toLocaleString()}</strong>.
          </div>
          <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
            {['Visa','Mastercard','MTN MoMo','Airtel Money'].map(m=>(
              <span key={m} style={{ fontSize:'0.7rem', padding:'4px 10px', borderRadius:20, background:'rgba(62,207,142,0.1)', border:'1px solid rgba(62,207,142,0.2)', color:'#3ecf8e', fontWeight:600 }}>{m}</span>
            ))}
          </div>
          <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', marginTop:10 }}>🔒 Secured by Pesapal · 256-bit SSL</div>
        </div>
      )}

      {errors.submit && (
        <div style={{ padding:'12px 14px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:8, fontSize:'0.84rem', color:'#ef4444' }}>
          ⚠ {errors.submit}
        </div>
      )}

      <div style={{ display:'flex', gap:10, marginTop:4 }}>
        <button onClick={onBack} style={backBtn}>← Back</button>
        <button onClick={onNext} disabled={submitting} className="btn btn-primary"
          style={{ flex:1, padding:'14px', fontSize:'0.95rem', fontWeight:700, justifyContent:'center', opacity:submitting?0.7:1 }}>
          {submitting ? 'Placing order…' : `Place Order · ${currency} ${total.toLocaleString()}`}
        </button>
      </div>
    </div>
  );
}

// ── MoMo Polling Component ────────────────────────────────
function MoMoPolling({ paymentId, phone, total, currency, onSuccess }) {
  const [status,  setStatus]  = useState('pending');
  const [seconds, setSeconds] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Count up timer
    timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);

    // Poll every 4 seconds
    pollRef.current = setInterval(async () => {
      try {
        const res  = await fetch(`${PAYMENTS_URL}/payments/${paymentId}/verify`);
        const data = await res.json();
        const s    = data.data?.status;
        setStatus(s);
        if (s === 'completed') {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          setTimeout(() => onSuccess(), 1500);
        }
        if (s === 'failed') {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
        }
      } catch { /* network hiccup — keep polling */ }
    }, 4000);

    return () => {
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
    };
  }, [paymentId]);

  const statusConfig = {
    pending:   { color:'#f9c80e', icon:'📱', label:'Waiting for approval…' },
    processing:{ color:'#f9c80e', icon:'⏳', label:'Processing payment…'   },
    completed: { color:'#3ecf8e', icon:'✓',  label:'Payment confirmed!'    },
    failed:    { color:'#ef4444', icon:'✕',  label:'Payment failed'        },
  };
  const cfg = statusConfig[status] || statusConfig.pending;

  return (
    <div style={{ padding:'24px', background:`${cfg.color}08`, border:`1px solid ${cfg.color}30`, borderRadius:16, textAlign:'center' }}>
      <div style={{ fontSize:'2.5rem', marginBottom:12 }}>{cfg.icon}</div>
      <div style={{ fontWeight:600, fontSize:'1rem', color:cfg.color, marginBottom:8 }}>{cfg.label}</div>
      {status === 'pending' || status === 'processing' ? (
        <>
          <p style={{ fontSize:'0.85rem', color:'var(--t-secondary)', lineHeight:1.7, marginBottom:12 }}>
            Check your phone <strong>{phone}</strong> for a MoMo USSD prompt.<br/>
            Enter your PIN to approve <strong>{currency} {total.toLocaleString()}</strong>.
          </p>
          <div style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>
            Waiting… {seconds}s
            <div style={{ height:2, background:'var(--border)', borderRadius:2, marginTop:8, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${Math.min((seconds/120)*100,100)}%`, background:cfg.color, borderRadius:2, transition:'width 1s linear' }}/>
            </div>
          </div>
        </>
      ) : status === 'failed' ? (
        <p style={{ fontSize:'0.85rem', color:'var(--t-secondary)' }}>The payment was not approved. Please try again.</p>
      ) : null}
    </div>
  );
}

// ── Step 4 — Confirmation ─────────────────────────────────
function ConfirmStep({ order, form, items, total, currency, momoPaymentId, onMoMoSuccess }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20, textAlign:'center' }}>
      <div style={{ width:80, height:80, borderRadius:'50%', background:'rgba(62,207,142,0.12)', border:'1px solid rgba(62,207,142,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto', fontSize:'2.5rem' }}>✓</div>
      <div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', fontWeight:600, marginBottom:8 }}>
          Order placed, <span style={{ color:'var(--gold)', fontStyle:'italic' }}>{form.full_name?.split(' ')[0]}!</span>
        </div>
        <p style={{ color:'var(--t-secondary)', fontSize:'0.9rem', lineHeight:1.8 }}>
          Your order <strong style={{ color:'var(--gold)', fontFamily:'monospace' }}>{order?.reference}</strong> has been received.
          A confirmation has been sent to <strong>{form.email}</strong>.
        </p>
      </div>

      {/* MoMo — live polling */}
      {form.payment_method === 'mtn_momo' && momoPaymentId && (
        <MoMoPolling
          paymentId={momoPaymentId}
          phone={form.momo_phone||form.phone}
          total={total}
          currency={currency}
          onSuccess={onMoMoSuccess}
        />
      )}

      {/* MoMo — fallback if no payment service */}
      {form.payment_method === 'mtn_momo' && !momoPaymentId && (
        <div style={{ padding:'16px', background:'rgba(249,200,14,0.06)', border:'1px solid rgba(249,200,14,0.2)', borderRadius:12, textAlign:'left' }}>
          <div style={{ fontWeight:600, marginBottom:6, fontSize:'0.9rem' }}>📱 Complete your payment</div>
          <p style={{ color:'var(--t-secondary)', fontSize:'0.85rem', lineHeight:1.7 }}>
            Check your phone <strong>{form.momo_phone||form.phone}</strong> for a MoMo USSD prompt.
            Enter your PIN to approve <strong>{currency} {total.toLocaleString()}</strong>.
          </p>
        </div>
      )}

      {/* Bank transfer */}
      {form.payment_method === 'bank_transfer' && (
        <div style={{ padding:'16px', background:'rgba(78,156,245,0.06)', border:'1px solid rgba(78,156,245,0.2)', borderRadius:12, textAlign:'left' }}>
          <div style={{ fontWeight:600, marginBottom:6, fontSize:'0.9rem' }}>🏦 Complete your bank transfer</div>
          <p style={{ color:'var(--t-secondary)', fontSize:'0.85rem', lineHeight:1.7 }}>
            Transfer <strong>{currency} {total.toLocaleString()}</strong> to MilleHills Ltd, BPR Bank,
            using reference <strong style={{ fontFamily:'monospace' }}>{order?.reference}</strong>. Allow up to 24 hours.
          </p>
        </div>
      )}

      {/* Order summary */}
      <div style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden', textAlign:'left' }}>
        <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', fontWeight:600, fontSize:'0.88rem' }}>Order Summary</div>
        {items.map((item,i) => (
          <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'10px 16px', borderBottom:'1px solid var(--border)', fontSize:'0.85rem' }}>
            <span>{item.name}{item.variant?` (${item.variant})`:''} ×{item.qty}</span>
            <span style={{ fontWeight:600 }}>{item.is_free?'Free':`${item.currency} ${Number(item.price*item.qty).toLocaleString()}`}</span>
          </div>
        ))}
        <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 16px', fontWeight:700 }}>
          <span>Total</span>
          <span style={{ color:'var(--gold)' }}>{currency} {total.toLocaleString()}</span>
        </div>
      </div>

      <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
        <Link to="/marketplace" className="btn btn-outline" style={{ padding:'12px 24px' }}>Continue Shopping</Link>
        <Link to="/dashboard"   className="btn btn-primary" style={{ padding:'12px 24px' }}>View My Orders →</Link>
      </div>
    </div>
  );
}

// ── MAIN CHECKOUT PAGE ────────────────────────────────────
export default function CheckoutPage() {
  const { items, total, currency, clearCart } = useCart();

  const [step,          setStep]         = useState(0);
  const [form,          setForm]         = useState({});
  const [errors,        setErrors]       = useState({});
  const [submitting,    setSubmitting]   = useState(false);
  const [order,         setOrder]        = useState(null);
  const [momoPaymentId, setMomoPaymentId]= useState(null);
  const [momoSuccess,   setMomoSuccess]  = useState(false);

  const hasPhysical = items.some(i => i.type === 'physical');

  // Pre-fill from logged-in user
  useEffect(() => {
    const token = localStorage.getItem('spa_token');
    if (!token) return;
    fetch(`${API_URL}/auth/me`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r=>r.ok?r.json():null).then(d => {
        const u = d?.data || d?.user;
        if (u) setForm(f => ({ ...f, full_name:u.name||f.full_name||'', email:u.email||f.email||'', phone:u.phone||f.phone||'' }));
      }).catch(()=>{});
  }, []);

  const validateInfo = () => {
    const errs = {};
    if (!form.full_name?.trim()) errs.full_name = 'Full name is required';
    if (!form.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Valid email required';
    if (!form.phone?.trim()) errs.phone = 'Phone number is required';
    if (hasPhysical && !form.address?.trim()) errs.address = 'Delivery address required for physical items';
    return errs;
  };

  const validatePayment = () => {
    const errs = {};
    if (!form.payment_method) errs.payment_method = 'Please select a payment method';
    if (form.payment_method === 'mtn_momo' && !form.momo_phone?.trim() && !form.phone?.trim())
      errs.momo_phone = 'MoMo phone number is required';
    return errs;
  };

  const handleNext = async () => {
    if (step === 0) { setStep(1); window.scrollTo({top:0}); return; }
    if (step === 1) {
      const errs = validateInfo();
      if (Object.keys(errs).length) { setErrors(errs); return; }
      setErrors({}); setStep(2); window.scrollTo({top:0}); return;
    }
    if (step === 2) {
      const errs = validatePayment();
      if (Object.keys(errs).length) { setErrors(errs); return; }
      setErrors({});
      setSubmitting(true);
      try {
        const token   = localStorage.getItem('spa_token');
        const headers = { 'Content-Type':'application/json', ...(token?{Authorization:`Bearer ${token}`}:{}) };

        // 1. Create order in main backend
        const res = await fetch(`${API_URL}/orders`, {
          method:'POST', headers,
          body: JSON.stringify({
            items: items.map(i => ({ id:i.id, name:i.name, type:i.type, slug:i.slug, price:i.price, qty:i.qty, currency:i.currency, variant:i.variant||null })),
            full_name:        form.full_name,
            email:            form.email,
            phone:            form.phone,
            shipping_address: form.address||null,
            notes:            form.notes||null,
            payment_method:   form.payment_method,
            momo_phone:       form.momo_phone||form.phone,
            total_amount:     total,
            currency,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error||'Order failed');
        const createdOrder = data.data;

        // 2. For MoMo — initiate real USSD push via payments service
        if (form.payment_method === 'mtn_momo') {
          try {
            const ppRes = await fetch(`${PAYMENTS_URL}/payments/initiate`, {
              method:'POST',
              headers: { 'Content-Type':'application/json' },
              body: JSON.stringify({
                flow:        'order',
                gateway:     'mtn_momo',
                amount:      total,
                currency:    currency === 'RWF' ? 'EUR' : currency, // sandbox uses EUR
                payerName:   form.full_name,
                payerEmail:  form.email,
                payerPhone:  form.momo_phone||form.phone,
                description: `MilleHills Order ${createdOrder.reference}`,
                metadata:    { order_reference: createdOrder.reference },
              }),
            });
            const ppData = await ppRes.json();
            if (ppData.data?.paymentId) {
              setMomoPaymentId(ppData.data.paymentId);
            }
          } catch(e) {
            console.warn('MoMo initiation failed — order saved, customer can retry:', e.message);
          }
          setOrder(createdOrder);
          clearCart();
          setStep(3);
          window.scrollTo({top:0});
          return;
        }

        // 3. For Pesapal — redirect to payment page
        if (form.payment_method === 'pesapal') {
          const ppRes = await fetch(`${API_URL}/payments/pesapal/initiate`, {
            method:'POST', headers,
            body: JSON.stringify({
              order_reference: createdOrder.reference,
              amount:          total,
              currency,
              email:           form.email,
              phone:           form.phone,
              first_name:      form.full_name?.split(' ')[0]||'',
              last_name:       form.full_name?.split(' ').slice(1).join(' ')||'',
              description:     `MilleHills Order ${createdOrder.reference}`,
            }),
          });
          const ppData = await ppRes.json();
          if (ppData.data?.redirect_url) {
            clearCart();
            window.location.href = ppData.data.redirect_url;
            return;
          }
          throw new Error('Payment initiation failed. Please try again.');
        }

        // 4. Bank transfer — just show confirmation
        setOrder(createdOrder);
        clearCart();
        setStep(3);
        window.scrollTo({top:0});
      } catch(e) {
        setErrors({ submit: e.message });
      } finally { setSubmitting(false); }
    }
  };

  const handleBack = () => { setStep(s=>s-1); window.scrollTo({top:0}); };
  const pct = (step / (STEPS.length-1)) * 100;

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-0)', paddingTop:'var(--nav-h)' }}>
      <div className="container" style={{ maxWidth:1100, padding:'clamp(32px,5vw,64px) clamp(16px,4vw,52px)' }}>

        {/* Header */}
        <div style={{ marginBottom:40 }}>
          <Link to="/marketplace" style={{ fontSize:'0.82rem', color:'var(--t-muted)', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6, marginBottom:16 }}>
            ← Back to Marketplace
          </Link>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(1.6rem,3vw,2.2rem)', fontWeight:700, marginBottom:8 }}>Checkout</h1>

          {/* Progress bar */}
          <div style={{ display:'flex', gap:0, marginTop:24, position:'relative' }}>
            <div style={{ position:'absolute', top:13, left:0, right:0, height:2, background:'var(--border)', zIndex:0 }}/>
            <div style={{ position:'absolute', top:13, left:0, height:2, background:'var(--gold)', zIndex:1, transition:'width 0.4s ease', width:`${pct}%` }}/>
            {STEPS.map((s,i) => {
              const done=i<step, active=i===step;
              return (
                <div key={s.id} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:8, position:'relative', zIndex:2 }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.72rem', fontWeight:700, background:done?'#3ecf8e':active?'var(--gold)':'var(--bg-2)', color:done||active?'#080a0d':'var(--t-muted)', border:`2px solid ${done?'#3ecf8e':active?'var(--gold)':'var(--border)'}`, transition:'all 0.3s' }}>
                    {done?'✓':s.icon}
                  </div>
                  <span style={{ fontSize:'0.68rem', color:active?'var(--gold)':done?'#3ecf8e':'var(--t-muted)', fontWeight:active?600:400, transition:'color 0.3s' }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main layout */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:32, alignItems:'start' }}>
          <div style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:20, padding:'28px 32px' }}>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', fontWeight:600, marginBottom:24 }}>
              {step===0&&'Review Your Cart'}
              {step===1&&'Your Details'}
              {step===2&&'Choose Payment'}
              {step===3&&(momoSuccess?'Payment Confirmed! 🎉':'Order Confirmed!')}
            </h2>
            {step===0 && <ReviewStep items={items} total={total} currency={currency} onNext={handleNext}/>}
            {step===1 && <InfoStep form={form} setForm={setForm} errors={errors} onNext={handleNext} onBack={handleBack} hasPhysical={hasPhysical}/>}
            {step===2 && <PaymentStep form={form} setForm={setForm} total={total} currency={currency} errors={errors} onNext={handleNext} onBack={handleBack} submitting={submitting}/>}
            {step===3 && <ConfirmStep order={order} form={form} items={order?.items||items} total={total} currency={currency} momoPaymentId={momoPaymentId} onMoMoSuccess={()=>setMomoSuccess(true)}/>}
          </div>

          {/* Order summary sidebar */}
          {step < 3 && (
            <div style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:20, padding:'24px', position:'sticky', top:'calc(var(--nav-h) + 20px)' }}>
              <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.95rem', marginBottom:16 }}>Order Summary</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
                {items.map((item,i) => (
                  <div key={i} style={{ display:'flex', gap:10, alignItems:'center' }}>
                    <div style={{ width:40, height:40, borderRadius:6, overflow:'hidden', background:'var(--bg-3)', flexShrink:0 }}>
                      {resolveMedia(item.image) ? <img src={resolveMedia(item.image)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', opacity:0.2 }}>◈</div>}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'0.8rem', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name}</div>
                      {item.variant && <div style={{ fontSize:'0.7rem', color:'var(--t-muted)' }}>{item.variant}</div>}
                    </div>
                    <div style={{ fontSize:'0.82rem', fontWeight:600, flexShrink:0, color:'var(--gold)' }}>
                      {item.is_free?'Free':`${item.currency} ${Number(item.price*item.qty).toLocaleString()}`}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:'1rem' }}>
                  <span>Total</span>
                  <span style={{ color:'var(--gold)' }}>{currency} {total.toLocaleString()}</span>
                </div>
                <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', marginTop:6 }}>
                  {items.length} item{items.length>1?'s':''} · {items.some(i=>i.type==='physical')?'Includes delivery':'Digital delivery'}
                </div>
              </div>
              <div style={{ marginTop:16, padding:'12px', background:'var(--bg-2)', borderRadius:8, display:'flex', flexDirection:'column', gap:6 }}>
                {['🔒 Secure checkout','📧 Email confirmation','💬 WhatsApp support'].map(b=>(
                  <div key={b} style={{ fontSize:'0.72rem', color:'var(--t-muted)' }}>{b}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}